import * as React from "react";
import Text from "app/styles/Text";
import { ReactComponent as ChevronDown } from "app/assets/icons/chevron-down.svg";
import { ReactComponent as SearchIcon } from "app/assets/icons/search.svg";
import { ReactComponent as CheckIcon } from "app/assets/icons/check.svg";

import { Popover, PopoverButton, PopoverPanel, PopoverPanelProps } from "@headlessui/react";
import InputField from "app/components/InputField";
import { FIREBASE_COIN_IMG_BASE_URL } from "app/constants/variables";
import { useAppState } from "app/providers/AppStateProvider";

interface SelectCurrencyMenuProps {
  supportedCurrencies:
    | Array<{ name: string; code: string; description?: string }>
    | undefined;
  handleCurrencyChange: (value: string) => void;
  value: string;
  children: React.ReactNode;
  noSearch?: boolean;
  dropdownClassName?: string;
  anchor?: PopoverPanelProps["anchor"];
}

export default function SelectCurrencyMenu({
  supportedCurrencies,
  handleCurrencyChange,
  value,
  children,
  noSearch,
  dropdownClassName,
  anchor = "bottom",
}: SelectCurrencyMenuProps) {
  const { displayMode } = useAppState();
  const [search, setSearch] = React.useState("");

  if (!supportedCurrencies || supportedCurrencies.length <= 1) return null;

  const filteredCurrencies = supportedCurrencies.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Popover>
      <PopoverButton tw="focus:outline-none focus:ring-0 w-full">
        {children || <ChevronDown tw="w-[24px] h-[24px] text-text-mid" />}
      </PopoverButton>

      <PopoverPanel
        transition
        anchor={anchor}
        className={dropdownClassName}
        tw="mt-[11px] bg-containment-primary p-[4px] rounded-[16px] shadow-left-soft transition duration-200 ease-in-out w-[var(--button-width)]"
      >
        <div tw="bg-containment-tertiary rounded-[12px] flex flex-col gap-[16px]">
          {!noSearch && (
            <InputField
              placeholder="Search currency"
              tw="w-full"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              prefix={<SearchIcon tw="w-[24px] h-[24px] text-text-high" />}
            />
          )}

          <div tw="max-h-[400px] overflow-auto flex flex-col gap-[8px]">
            {filteredCurrencies.map((currency) => (
              <button
                key={currency.code}
                type="button"
                tw="w-full flex items-center gap-[16px] p-[16px] rounded-[12px] hover:bg-gray-50 focus:outline-none focus:ring-0"
                onClick={() => handleCurrencyChange(currency.code)}
              >
                <img
                  src={`${FIREBASE_COIN_IMG_BASE_URL}${currency.code}.svg?alt=media`}
                  alt={currency.name}
                  tw="w-[40px] h-[40px] rounded-full"
                />
                <div tw="flex-1 text-left">
                  <Text semiBold medium>
                    {currency.code}
                  </Text>
                  <Text subdued tw="text-[12px]">
                    {currency.description ?? currency.name}
                  </Text>
                </div>

                {value === currency.code && (
                  <CheckIcon tw="w-[24px] h-[24px] text-text-mid" />
                )}
              </button>
            ))}
          </div>
        </div>
      </PopoverPanel>
      {/* </Transition> */}
    </Popover>
  );
}
